import type { ListCriteriaNode } from '../../api/lists-contracts';
import { metricsUpdatesApi } from '../../api/metrics-updates-api';
import type { IMetricsCountsRequest, IMetricsEditionKey } from '../../api/metrics-updates-contracts';
import { useAdminData } from '../../components/admin-page';
import { signalsOf, type CardVariant, type IStateSignal, type SignalSpec } from '../../components/state-cards';
import { FIRM_STATUS, REQUESTS } from '../../metrics-updates';
import { ASSIGNED_TO } from '../lists/editing';
import type { IWorkSignal } from '../lists/work-page/signals';
import type { StageView } from './stage-strip';

/**
 * The stage tiles' live signals (SR-456): survey-api's `/metrics-updates/{survey}/counts`, one read
 * over the editions and "DD call done" the tiles count under — each tile's list with one more
 * condition, keyed `<view>.<signal>`. The updates lists differ by the firm's status alone, so the
 * read keeps every status a list names and each key its own; the exceptions' keys name their tab.
 * *Owed by firm files* counts firms, which this read does not list: it has none.
 */

type Kind = 'not_asked' | 'asked' | 'unassigned';

const SIGNALS: Record<Kind, SignalSpec & { leaves: ListCriteriaNode[] }> = {
  not_asked: {
    key: 'not_asked',
    label: 'not asked yet',
    tone: 'attention',
    leaves: [
      {
        op: 'or',
        children: [
          { field: REQUESTS, op: 'empty' },
          { field: REQUESTS, op: 'eq', value: 0 }
        ]
      }
    ]
  },
  asked: { key: 'asked', label: 'asked, no reply', tone: 'info', leaves: [{ field: REQUESTS, op: 'gte', value: 1 }] },
  unassigned: { key: 'unassigned', label: 'unassigned', tone: 'info', leaves: [{ field: ASSIGNED_TO, op: 'empty' }] }
};

/** Each signal as a filter of the page — a branch of the signals' group in the list's. */
export const STAGE_SIGNALS: readonly IWorkSignal[] = Object.values(SIGNALS).map(({ key, label, leaves }) => ({
  key,
  words: label,
  leaves
}));

/** Each tile's signals, the one a compact tile shows first. */
const BY_VIEW: Record<StageView, Kind[]> = {
  direct: ['not_asked', 'asked', 'unassigned'],
  unknown: ['unassigned', 'not_asked'],
  firms: [],
  exceptions: ['unassigned', 'not_asked']
};

/** The firm statuses each updates list keeps. */
const FIRMS: Partial<Record<StageView, Array<'direct' | 'not_set' | 'no_firm'>>> = {
  direct: ['direct'],
  unknown: ['not_set', 'no_firm']
};

/** The tiles' signals by view, null until read; none while no edition is ticked. A compact tile
 *  shows its first and those `on`. */
export function useStageSignals({
  survey,
  views,
  editions,
  ddDone,
  firmApplies,
  variant,
  enabled,
  counted,
  on
}: {
  survey: string;
  views: readonly StageView[];
  editions: IMetricsEditionKey[] | undefined;
  /** "DD call done" as the updates tab sends it, undefined where it does not apply. */
  ddDone: boolean | undefined;
  firmApplies: boolean;
  variant: CardVariant;
  enabled: boolean;
  counted: string;
  on: (view: string, key: string) => boolean;
}): Nullable<Record<string, IStateSignal[]>> {
  const asked = JSON.stringify([survey, views, editions ?? null, ddDone ?? null, firmApplies, enabled, counted]);
  const read = useAdminData<Nullable<{ asked: string; counts: Record<string, number> }>>(
    async (signal) => {
      if (!enabled) return null;
      const counts: IMetricsCountsRequest['counts'] = views.flatMap((view) => {
        const firm: ListCriteriaNode[] =
          firmApplies && FIRMS[view] ? [{ field: FIRM_STATUS, op: 'in', value: FIRMS[view] }] : [];
        return BY_VIEW[view].map((kind) => ({
          key: `${view}.${kind}`,
          criteria: { version: 1, op: 'and', children: [...firm, ...SIGNALS[kind].leaves] },
          ...(view === 'exceptions' ? { tab: 'exceptions' as const } : {})
        }));
      });
      if (counts.length === 0) return { asked, counts: {} };
      const body: IMetricsCountsRequest = {
        ...(editions ? { editions } : {}),
        tab: 'updates',
        firm: ['direct', 'not_set', 'no_firm'],
        ddDone,
        counts
      };
      return { asked, counts: (await metricsUpdatesApi.counts(survey, body, signal)).counts };
    },
    [asked]
  );
  if (read.data?.asked !== asked) return null;
  const { counts } = read.data;
  return Object.fromEntries(
    views.map((view) => [
      view,
      signalsOf(
        counts,
        `${view}.`,
        BY_VIEW[view]
          .filter((kind, index) => variant === 'detailed' || index === 0 || on(view, kind))
          .map((kind) => SIGNALS[kind])
      )
    ])
  );
}
